import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Download, ChevronDown, Droplets, Wind, Activity, Radio, Wifi, Sparkles, Box } from "lucide-react";
import { ASSETS, PROTOTYPE_GALLERY, EXPLODE_VIDEO, CAD_OVERLAY } from "@/lib/assets";
import { SectionHeading, Reveal } from "@/components/Section";
import { HeroStoryProvider, HeroHeadline, HeroProgress } from "@/components/HeroStory";
import RFIDSection from "@/components/RFIDSection";
import ZoomImage from "@/components/ZoomImage";
import { Product360 } from "@/components/Product360";
import { ValveViewer } from "@/components/ValveViewer";
import BandExplorer from "@/components/BandExplorer";
import { SEO } from "@/components/SEO";
import { PAGE_SEO } from "@/lib/seoData";

const BEAT = "block text-3xl sm:text-5xl md:text-6xl font-light leading-[1.06]";
function beat(pre, accent, post = "") {
  return (
    <span className={BEAT}>
      {pre}
      <span className="text-accent">{accent}</span>
      {post}
    </span>
  );
}

// Consolidated "essence of the technology" beats, settling on the page title.
const TECH_STEPS = [
  { dur: 4500, headline: beat("A ", "simple", " idea.") },
  { dur: 5000, headline: beat("A very ", "difficult problem", " to solve.") },
  {
    dur: 7000,
    headline: (
      <span className="block text-2xl sm:text-4xl md:text-5xl font-light leading-[1.18] max-w-4xl">
        How do you safely carry liquid, lotion or gel on the wrist and dispense
        it <span className="text-accent">exactly when you want it?</span>
      </span>
    ),
  },
  { dur: 5000, headline: beat("WearSpray ", "solved it", ".") },
  {
    dur: null,
    headline: (
      <span className="block font-light tracking-tight leading-[1.08] max-w-2xl">
        <span className="block text-3xl sm:text-4xl md:text-5xl">Patented Liquid Delivery</span>
        <span className="block text-3xl sm:text-4xl md:text-5xl text-accent mt-1">Engineered for</span>
        <span className="block text-3xl sm:text-4xl md:text-5xl text-accent">the Human Body in Motion.</span>
      </span>
    ),
  },
];

const WIREFRAME_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/t11m7nbf_wireframe%20reservoir%201.jpg";
const REAL_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/22rcgddf_wireframe%20reservoir%202.jpg";
const SCIENTISTS_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/6qgetq4f_new%20science%202.jpg";
const PLAIN_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/8xxj23o3_new%20science%201.jpg";

const EXPLODE_MS = 4600; // exploded view holds at full glory, then dissolves
const FRAME_MS = 4200; // hold per reveal frame (wireframe / real / scientists)

const HERO_SPARKS = [
  { x: -18, y: -10, d: 0 },
  { x: 16, y: -14, d: 0.12 },
  { x: -12, y: 14, d: 0.2 },
  { x: 20, y: 8, d: 0.28 },
  { x: 0, y: -20, d: 0.36 },
  { x: 6, y: 18, d: 0.44 },
];

// Rising light particles for the science "astral projection" field (offsets from the glowing face).
const ASTRAL_PARTICLES = [
  { x: -8, size: 6, dur: 5.5, delay: 0, drift: 3 },
  { x: -3, size: 4, dur: 6.5, delay: 0.8, drift: -2 },
  { x: 2, size: 8, dur: 5, delay: 1.4, drift: 2 },
  { x: 7, size: 5, dur: 7, delay: 0.4, drift: -3 },
  { x: 11, size: 4, dur: 6, delay: 2, drift: 2 },
  { x: -12, size: 5, dur: 6.2, delay: 1.1, drift: 3 },
  { x: 1, size: 7, dur: 5.8, delay: 2.6, drift: -1 },
  { x: 5, size: 3, dur: 7.4, delay: 3.1, drift: 2 },
  { x: -6, size: 4, dur: 6.8, delay: 3.6, drift: -2 },
  { x: 9, size: 6, dur: 5.4, delay: 1.9, drift: 1 },
  { x: -15, size: 3, dur: 6.6, delay: 2.3, drift: 2 },
  { x: 14, size: 5, dur: 5.9, delay: 0.6, drift: -2 },
];

// Exploded view -> dissolve -> wireframe -> real device -> plain wearable -> scientists, looping.
function TechHeroBackground() {
  const [phase, setPhase] = useState("explode"); // "explode" | "reveal"
  const [frameIdx, setFrameIdx] = useState(0); // 0 wireframe, 1 real, 2 scientists
  const [burst, setBurst] = useState(0); // retriggers the energy burst on every hand-off
  const frameRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setPhase("reveal");
      setBurst((b) => b + 1);
    }, EXPLODE_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (phase !== "reveal") return undefined;
    const id = setInterval(() => {
      const cur = frameRef.current;
      const next = (cur + 1) % 5;
      frameRef.current = next;
      setFrameIdx(next);
      setBurst((b) => b + 1);
    }, FRAME_MS);
    return () => clearInterval(id);
  }, [phase]);

  const isExplode = phase === "explode";
  const isReveal = phase === "reveal";

  return (
    <div className="absolute inset-0 z-0" data-testid="tech-hero-bg">
      {/* Exploded view, translucent blue components with patent wireframe overlay */}
      <video
        data-testid="explode-video"
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          opacity: isExplode ? 1 : 0,
          transition: "opacity 1500ms ease",
          filter: "brightness(1.35) saturate(1.12)",
        }}
      >
        <source src={EXPLODE_VIDEO} type="video/mp4" />
      </video>
      <img loading="lazy"
        src={CAD_OVERLAY}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-contain mix-blend-screen animate-blueprint-bright pointer-events-none p-10 md:p-20"
        style={{
          opacity: isExplode ? 1 : 0,
          transition: "opacity 1500ms ease",
          filter: "drop-shadow(0 0 10px rgba(0,229,255,0.55)) brightness(1.25)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: isExplode ? 0.14 : 0,
          transition: "opacity 1500ms ease",
          backgroundImage:
            "linear-gradient(rgba(0,229,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,255,0.5) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      {/* Frame 0, wireframe of the reservoir */}
      <img
        src={WIREFRAME_IMG}
        alt=""
        aria-hidden="true"
        loading="eager"
        decoding="async"
        data-testid="tech-wireframe"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ opacity: isReveal && frameIdx === 0 ? 1 : 0, transition: "opacity 1400ms ease" }}
      />
      {/* Frame 1, the real device */}
      <img
        src={REAL_IMG}
        alt=""
        aria-hidden="true"
        loading="eager"
        decoding="async"
        data-testid="tech-real"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ opacity: isReveal && frameIdx === 1 ? 1 : 0, transition: "opacity 1400ms ease", objectPosition: "center 68%" }}
      />
      {/* Frame 2, the wearable on black (plain, before the scientists appear) */}
      <img
        src={PLAIN_IMG}
        alt=""
        aria-hidden="true"
        loading="eager"
        decoding="async"
        data-testid="tech-science-1"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ opacity: isReveal && frameIdx === 2 ? 1 : 0, transition: "opacity 1400ms ease", objectPosition: "center 74%", transform: "scale(1.12)" }}
      />
      {/* Frame 3, scientists suddenly revealed behind the wearable */}
      <img
        src={SCIENTISTS_IMG}
        alt=""
        aria-hidden="true"
        loading="eager"
        decoding="async"
        data-testid="tech-science-2"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ opacity: isReveal && frameIdx === 3 ? 1 : 0, transition: "opacity 1400ms ease", objectPosition: "center 74%", transform: "scale(1.12)" }}
      />

      {/* Frame 4, laser scan of the wearable, capturing its essence */}
      <video
        data-testid="tech-laser"
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        style={{ opacity: isReveal && frameIdx === 4 ? 1 : 0, transition: "opacity 1400ms ease" }}
      >
        <source src="/video/laser-hero.mp4" type="video/mp4" />
      </video>

      {/* Blue astral projections, alive while the scientists study the wearable */}
      <div
        className="absolute inset-0 pointer-events-none mix-blend-screen"
        style={{ opacity: isReveal && frameIdx === 3 ? 1 : 0, transition: "opacity 1200ms ease" }}
        data-testid="tech-astral-field"
      >
        <div className="absolute" style={{ left: "55.5%", top: "50%" }}>
          {/* Emanating pulse rings */}
          {[0, 1, 2, 3].map((r) => (
            <motion.div
              key={`ring-${r}`}
              className="absolute rounded-full"
              style={{
                width: "17vw",
                height: "17vw",
                marginLeft: "-8.5vw",
                marginTop: "-8.5vw",
                border: "1px solid rgba(0,229,255,0.45)",
                boxShadow: "0 0 30px rgba(0,229,255,0.35), inset 0 0 24px rgba(0,229,255,0.25)",
              }}
              animate={{ opacity: [0, 0.6, 0], scale: [0.25, 1.7, 2.4] }}
              transition={{ duration: 5.2, delay: r * 1.3, repeat: Infinity, ease: "easeOut" }}
            />
          ))}

          {/* Breathing core bloom */}
          <motion.div
            className="absolute rounded-full blur-2xl"
            style={{
              width: "24vw",
              height: "24vw",
              marginLeft: "-12vw",
              marginTop: "-12vw",
              background: "radial-gradient(circle, rgba(90,200,255,0.5), rgba(0,229,255,0.12) 45%, transparent 68%)",
            }}
            animate={{ opacity: [0.3, 0.7, 0.3], scale: [0.9, 1.18, 0.9] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          />

          {/* Slow orbiting halo */}
          <motion.div
            className="absolute rounded-full"
            style={{
              width: "20vw",
              height: "20vw",
              marginLeft: "-10vw",
              marginTop: "-10vw",
              background: "conic-gradient(from 0deg, transparent 0deg, rgba(0,229,255,0.4) 60deg, transparent 140deg, rgba(120,210,255,0.3) 250deg, transparent 320deg)",
              filter: "blur(6px)",
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
          />

          {/* Rising light particles */}
          {ASTRAL_PARTICLES.map((p) => (
            <motion.span
              key={`ap-${p.x}-${p.size}`}
              className="absolute rounded-full"
              style={{
                width: p.size,
                height: p.size,
                background: "radial-gradient(circle, #ffffff, #7fe9ff 55%, transparent 78%)",
                boxShadow: "0 0 10px rgba(0,229,255,0.9)",
              }}
              initial={{ opacity: 0 }}
              animate={{
                opacity: [0, 1, 0],
                x: [`${p.x}vw`, `${p.x + p.drift}vw`],
                y: ["7vh", "-17vh"],
              }}
              transition={{ duration: p.dur, delay: p.delay, repeat: Infinity, ease: "easeOut" }}
            />
          ))}
        </div>
      </div>


      {/* Continuous blue aura, breathing */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[62vw] h-[62vh] rounded-full blur-3xl pointer-events-none mix-blend-screen"
        style={{
          background:
            "radial-gradient(circle, rgba(0,229,255,0.22), rgba(0,229,255,0.05) 50%, transparent 70%)",
        }}
        animate={{ opacity: [0.45, 0.8, 0.45], scale: [0.95, 1.05, 0.95] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Energy burst on every crossfade */}
      <AnimatePresence>
        <motion.div
          key={`burst-${burst}`}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none mix-blend-screen"
          style={{
            width: "40vw",
            height: "40vw",
            background:
              "radial-gradient(circle, rgba(0,229,255,0.6), rgba(90,190,255,0.2) 40%, transparent 68%)",
          }}
          initial={{ opacity: 0, scale: 0.35 }}
          animate={{ opacity: [0, 0.95, 0], scale: [0.35, 1.4, 1.95] }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
      </AnimatePresence>

      {/* Little sparks of energy */}
      <div key={`sparks-${burst}`} className="absolute inset-0 pointer-events-none">
        {HERO_SPARKS.map((s) => (
          <motion.span
            key={`${s.x}-${s.y}`}
            className="absolute top-1/2 left-1/2 w-2 h-2 rounded-full"
            style={{
              background: "radial-gradient(circle, #ffffff, #7fe9ff 55%, transparent 70%)",
              boxShadow: "0 0 12px rgba(0,229,255,0.9)",
            }}
            initial={{ opacity: 0, x: 0, y: 0, scale: 0 }}
            animate={{
              opacity: [0, 1, 0],
              x: `${s.x}vw`,
              y: `${s.y}vh`,
              scale: [0, 1, 0.2],
            }}
            transition={{ duration: 1.3, delay: s.d, ease: "easeOut" }}
          />
        ))}
      </div>

      {/* Legibility scrims */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/55 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-[#0a0a0a]/40" />
    </div>
  );
}

function TechHero() {
  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="technology-hero-section"
    >
      <TechHeroBackground />

      <HeroStoryProvider steps={TECH_STEPS}>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-28">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-mono text-xs tracking-[0.25em] uppercase text-accent mb-6"
            data-testid="technology-hero"
          >
            The Architecture of Protection
          </motion.p>

          <HeroHeadline className="max-w-4xl h-[160px] sm:h-[230px] md:h-[290px]" />

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-body text-lg md:text-xl text-zinc-300 mt-6 max-w-2xl leading-relaxed"
          >
            Three engineered components work together to make liquid, lotion or
            gel instantly accessible from the body, designed for frequent, easy
            reapplication.
          </motion.p>

          <HeroProgress className="mt-10 max-w-md" />
        </div>

        {/* Fig caption */}
        <div className="absolute bottom-6 left-0 w-full z-10 pointer-events-none">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <span className="font-mono text-xs text-zinc-400 tracking-[0.2em] uppercase">
              Fig. 01 · System Architecture · Exploded View
            </span>
          </div>
        </div>

        {/* Scroll cue */}
        <div
          className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2 pointer-events-none"
          data-testid="tech-scroll-cue"
        >
          <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-400">
            Scroll to explore
          </span>
          <ChevronDown size={18} className="text-accent animate-scroll-cue" />
        </div>
      </HeroStoryProvider>
    </section>
  );
}

export default function Technology() {
  return (
    <div data-testid="technology-page">
      <SEO {...PAGE_SEO["/technology"]} path="/technology" />
      <TechHero />

      {/* INTERACTIVE 360 · SPIN THE BAND */}
      <Product360 />

      {/* DEEP DIVE 1 / VALVE */}
      <section className="border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <Reveal>
            <span className="font-mono text-sm text-accent block mb-6">
              01 / VALVE
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight">
              The valve that breathes.
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6">
              Building a valve and gate that could breathe reliably in the tiny
              footprint of the wrist was a formidable challenge. This breathing
              mechanism is our core patent, what lets a liquid, lotion, or
              gel dispense smoothly and consistently from a sealed reservoir worn
              on the body. Simple to describe, exceptionally hard to engineer, and
              the foundation of everything WearSpray builds.
            </p>
            <ValveViewer>
              <button
                type="button"
                data-testid="tech-valve-interactive-trigger"
                className="inline-flex items-center gap-2.5 font-body text-sm font-medium text-accent border border-accent/40 rounded-full px-5 py-3 mt-8 hover:bg-accent/10 transition-colors"
              >
                <Box size={16} />
                Explore the interactive 3D model
              </button>
            </ValveViewer>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="relative border border-zinc-800 bg-[#0d0d0d] overflow-hidden">
              <ZoomImage
                src={ASSETS.valveCrossSection}
                alt="Cross-section of the WearSpray breathing valve and reservoir"
                className="w-full max-h-[60vh] object-contain"
                caption="Fig. 02 · Valve & Reservoir Cross-Section"
                testId="zoom-valve"
              />
              <div className="absolute bottom-0 left-0 p-5 pointer-events-none">
                <span className="font-mono text-xs text-zinc-500 tracking-[0.2em] uppercase">
                  Fig. 02 · Valve & Reservoir Cross-Section
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* DEEP DIVE 2 / RESERVOIR */}
      <section className="border-t border-zinc-800 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <Reveal>
            <span className="font-mono text-sm text-accent block mb-6">
              02 / RESERVOIR
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight">
              One reservoir. Built for real formulations.
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6">
              The refillable reservoir is engineered from recyclable low-density
              polyethylene (LDPE), a durable, collapsible core that
              maintains its shape and breathes for smooth, controlled flow. It is
              instantly refillable by hand, with no secondary device required, and
              chemically compatible across a broad range of real personal care
              products.
            </p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {["Sunscreen", "Mosquito Repellent", "Sanitizer", "Skincare", "Fragrance", "Lotions & Gels"].map((t) => (
                <li key={t} className="font-mono text-sm text-zinc-300 flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-[#00e5ff]" />
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* DEEP DIVE 3 / FLEXIBILITY & PERFORMANCE */}
      <section className="border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative border border-zinc-800 bg-[#0d0d0d] overflow-hidden">
              <ZoomImage
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/0m85ukii_fill%20volume%202.jpg"
                alt="Formulation being poured into the open WearSpray reservoir, with red and blue reservoirs and adjustable bands"
                className="w-full h-[460px] object-cover"
                caption="Flexible reservoir architecture"
                testId="zoom-fill-volume"
              />
            </div>
            <p className="font-body text-sm text-zinc-500 leading-relaxed mt-4">
              <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-400 block mb-1.5">
                Flexible reservoir architecture
              </span>
              Engineered contours and calibrated wall thicknesses allow the
              reservoir face to compress, transfer force to the formulation, and
              return to its original position after actuation. This responsive
              geometry supports controlled dispensing across a range of liquids,
              lotions, and gels.
            </p>
            <a
              href="/docs/WearSpray-LDPE-Material-Specifications.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ldpe-datasheet-link"
              className="group inline-flex items-center gap-2 mt-5 font-mono text-xs tracking-[0.15em] uppercase text-accent border border-accent/40 px-4 py-3 hover:bg-accent hover:text-black transition-colors"
            >
              <Download size={15} strokeWidth={1.8} />
              Download the LDPE Material Specifications (PDF)
            </a>
          </Reveal>
          <Reveal delay={0.12}>
            <span className="font-mono text-sm text-accent block mb-6">
              03 / PERFORMANCE
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight leading-tight">
              Engineered flexibility. Controlled performance.
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6">
              Carefully tuned contours and wall thickness create a responsive
              spring effect within the reservoir itself, no separate spring
              or pump required. Lab testing and several years of real-world use
              confirm its durability and structural performance across liquids,
              lotions, and gels.
            </p>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-4">
              That controlled compression moves the formulation through the valve
              and outlet: a light press delivers a measured application, firmer
              pressure gives greater force when needed.
            </p>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-4">
              At just over one fluid ounce, the reservoir offers meaningful
              capacity in a compact wrist-worn format, in liquid testing, a
              full fill produced roughly 99 light-press streams, though real
              applications vary with viscosity, pressure, and dose.
            </p>
            <div className="grid grid-cols-2 gap-px mt-8 bg-zinc-800 border border-zinc-800">
              <div className="bg-[#0d0d0d] p-6">
                <span className="font-heading text-3xl md:text-4xl font-light text-white">~1 fl oz</span>
                <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-500 block mt-2">
                  Fill Volume
                </span>
              </div>
              <div className="bg-[#0d0d0d] p-6">
                <span className="font-heading text-3xl md:text-4xl font-light text-[#00e5ff]">~99</span>
                <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-500 block mt-2">
                  Light-Press Streams / Fill
                </span>
              </div>
            </div>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-8">
              The result is a refillable dispensing system that combines
              flexibility, capacity, controlled output, and repeated access
              throughout the day.
            </p>
          </Reveal>
        </div>
      </section>

      {/* IT LOOKS SIMPLE NOW */}
      <section
        className="border-t border-zinc-800 bg-[#0a0a0a] relative overflow-hidden"
        data-testid="tech-simplicity-band"
      >
        <div
          className="absolute -top-1/4 left-1/2 -translate-x-1/2 w-[70vw] h-[60vh] rounded-full blur-3xl opacity-25 pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(0,229,255,0.16), transparent 65%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-40 text-center">
          <Reveal>
            <h2 className="font-heading text-4xl sm:text-6xl md:text-7xl font-light tracking-tighter leading-[1.02]">
              It looks simple now.
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="font-heading text-2xl sm:text-4xl md:text-5xl font-light tracking-tight leading-tight mt-6 text-accent">
              That is what great engineering does.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ENGINEERING WALKTHROUGH LINK */}
      <section className="border-t border-zinc-800 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <Reveal>
            <div className="border border-zinc-800 bg-black p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="max-w-2xl">
                <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent block mb-4">
                  From the Workshop
                </span>
                <p className="font-heading text-2xl md:text-3xl font-light tracking-tight leading-snug text-zinc-100">
                  Many specialists looked at the problem.
                  <span className="block text-zinc-400">It was difficult.</span>
                  <span className="block text-accent">A working solution emerged.</span>
                </p>
                <p className="font-body text-zinc-400 text-base leading-relaxed mt-5">
                  During prototype staging and design, we produced a technical
                  walkthrough for the engineers and injection-molding specialists
                  at our manufacturing facility to review.
                </p>
                <p className="font-body text-zinc-400 text-base leading-relaxed mt-4">
                  At the time we were testing a range of formulations, from thin
                  liquids to lotions and gels, to understand how each behaved
                  through the valve. This particular demonstration used a sanitizing
                  liquid, and the goal was simple: see how many short, controlled
                  bursts a single fill could deliver. The answer turned out to be
                  surprisingly high, a strong early signal for the reservoir and
                  valve working as one.
                </p>
              </div>
              <a
                href="https://youtu.be/NfjMRFp2-w4"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="engineering-walkthrough-link"
                className="group inline-flex items-center gap-2 shrink-0 font-body text-sm font-medium bg-[#00e5ff] text-black px-7 py-4 hover:bg-[#00b3cc] transition-colors"
              >
                Watch the Engineering Walkthrough
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* REAL PROTOTYPE SHOWCASE */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 border-t border-zinc-800">
        <SectionHeading
          overline="From Product to Platform"
          title="Real hardware. Working today."
          subtitle="This is the core utility: the refillable reservoir, the breathing valve, and the adjustable band (Velcro or silicone). The Safe Band Pro shown here is that utility in its purest form, with no styling applied. Everything built on top of it, the look, the feel, the design, is endless."
          testId="prototype-heading"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
          <Reveal>
            <div className="relative border border-zinc-800 bg-[#0d0d0d] overflow-hidden">
              <ZoomImage
                src={ASSETS.realReservoirVelcro}
                alt="Real WearSpray reservoir with removable cap on an adjustable Velcro band"
                className="w-full h-[420px] object-cover"
                caption="Refillable reservoir + adjustable Velcro band"
                testId="zoom-real-reservoir"
              />
              <span className="absolute top-4 right-4 z-10 font-mono text-[10px] tracking-[0.15em] uppercase text-[#00e5ff] bg-black/60 backdrop-blur-sm border border-[#00e5ff]/40 px-3 py-1.5 pointer-events-none">
                Available in Velcro & Silicone
              </span>
              <div className="absolute bottom-0 left-0 p-5 pointer-events-none">
                <span className="font-mono text-xs text-zinc-400 tracking-[0.2em] uppercase block">
                  The Reservoir
                </span>
                <span className="font-mono text-[11px] text-[#00e5ff] tracking-[0.15em] uppercase block mt-1.5">
                  Velcro band · built for water sports
                </span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative border border-zinc-800 bg-[#0d0d0d] overflow-hidden">
              <ZoomImage
                src={ASSETS.realValveMacro}
                alt="Real macro of the breathing valve with radial micro-perforations"
                className="w-full h-[420px] object-cover"
                caption="The breathing valve · radial micro-perforations"
                testId="zoom-real-valve"
              />
              <div className="absolute bottom-0 left-0 p-5 pointer-events-none">
                <span className="font-mono text-xs text-zinc-400 tracking-[0.2em] uppercase">
                  The Breathing Valve
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Component gallery */}
        <Reveal delay={0.1}>
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mt-16 mb-6">
            Component Gallery
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-800 border border-zinc-800">
            {PROTOTYPE_GALLERY.map((g, i) => (
              <ZoomImage
                key={g.src}
                src={g.src}
                alt={`WearSpray component · ${g.label}`}
                className="w-full h-40 md:h-48 object-cover bg-[#0d0d0d]"
                caption={g.label}
                testId={`zoom-gallery-${i}`}
              />
            ))}
          </div>
          <p className="font-mono text-xs text-zinc-600 mt-4">
            Working hardware, shown across reservoir and valve components. Production
            units feature refined finishes and an optional silicone band.
          </p>
        </Reveal>

        {/* Form is open, utility is fixed */}
        <Reveal delay={0.1}>
          <div className="mt-16 border border-zinc-800 bg-[#0d0d0d] p-8 md:p-12 relative overflow-hidden">
            <div
              className="absolute -top-1/3 right-[-10%] w-[40vw] h-[40vw] rounded-full blur-3xl opacity-40 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(0,229,255,0.18), transparent 60%)" }}
            />
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent block mb-5">
              The Utility Is Fixed. The Form Is Open.
            </span>
            <h3 className="font-heading text-2xl md:text-4xl font-light tracking-tight leading-snug max-w-4xl">
              Safe Band Pro is the baseline: the core utility with nothing added.
              What sits on top can be almost anything you can imagine.
            </h3>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6 max-w-3xl">
              The Velcro version carries no design language on purpose, it
              shows the mechanics in their purest form. With the reservoir and
              breathing valve as the only fixed elements, the housing, materials,
              silhouette, and finish become a blank canvas: a rugged field band, a
              luxury cuff, a fashion accessory, or a fully integrated smartwatch.
              Partners design the experience; the technology simply powers it.
            </p>
          </div>
        </Reveal>
      </section>

      <BandExplorer variant="technology" />

      {/* ECLECTIC CAPABILITIES · LASER SCAN BANNER + BENTO (consolidated) */}
      <section className="border-t border-zinc-800 bg-[#080808] relative overflow-hidden" data-testid="technology-capabilities">
        {/* laser scan hero banner */}
        <div className="relative h-[62vh] min-h-[440px] w-full overflow-hidden" data-testid="capabilities-laser-banner">
          <video
            src="/video/laser-wrist.mp4"
            poster="/video/laser-wrist-poster.jpg"
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="capabilities-laser-video"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-transparent to-black/40" />
          <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2.5 border border-[#ff6fae]/40 bg-[#ff6fae]/[0.08] rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 rounded-full bg-[#ff6fae] animate-pulse" />
                <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#ff8fc0]">In Development</span>
              </span>
              <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter leading-[1.03] text-white">
                One band.<br />A world of functions.
              </h2>
              <p className="font-body text-zinc-300 text-lg leading-relaxed mt-6 max-w-xl">
                A wearable is only the beginning. Under the surface, WearSpray is a
                refillable reservoir that carries liquids, lotions or gels, with a
                growing set of senses being built on top of it.
              </p>
            </div>
          </div>
        </div>

        {/* bento of functions */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
            {[
              { Icon: Droplets, t: "Holds liquids, lotions & gels", d: "The refillable reservoir dispenses thin liquids, lotions and gels from a single fill.", tag: "Available Today", tagC: "#00e5ff" },
              { Icon: Wind, t: "Breathing valve", d: "A micro-perforated valve meters each controlled burst and reseals between uses.", tag: "Available Today", tagC: "#00e5ff" },
              { Icon: Activity, t: "Biometric sensing", d: "Heart rate, skin temperature and activity, read at the wrist to time your care.", tag: "In Development", tagC: "#ff6fae" },
              { Icon: Radio, t: "Embedded RFID", d: "Tap-to-identify for access, inventory and secure hand-offs across a fleet of bands.", tag: "Research Concept", tagC: "#a06bff" },
              { Icon: Wifi, t: "Connected context", d: "Real-time UV, weather and location from your phone, woven into the right moment.", tag: "In Development", tagC: "#ff6fae" },
              { Icon: Sparkles, t: "Adaptive status glow", d: "A proposed visual reminder on the reservoir face tied to your application schedule, not a measurement of actual protection.", tag: "Research Concept", tagC: "#a06bff" },
            ].map((x, i) => (
              <Reveal key={x.t} delay={(i % 3) * 0.06}>
                <div className="group relative bg-[#0b0b0b] p-7 md:p-8 h-full hover:bg-[#101010] transition-colors duration-300" data-testid={`capability-tile-${i}`}>
                  <div className="flex items-center justify-between mb-6">
                    <span
                      className="inline-flex items-center justify-center w-11 h-11 rounded-xl border"
                      style={{ borderColor: `${x.tagC}55`, background: `${x.tagC}14` }}
                    >
                      <x.Icon size={20} style={{ color: x.tagC }} strokeWidth={1.7} />
                    </span>
                    <span className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border" style={{ color: x.tagC, borderColor: `${x.tagC}40` }}>
                      {x.tag}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-medium text-white leading-tight">{x.t}</h3>
                  <p className="font-body text-zinc-400 text-sm leading-relaxed mt-2.5">{x.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* materials & build */}
          <div className="mt-14 border-t border-zinc-800 pt-10" data-testid="capabilities-materials">
            <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-zinc-500 block mb-6">Materials &amp; build</span>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
              {[
                ["Refillable LDPE reservoir", "A durable, collapsible low-density polyethylene core holds your chosen formula and maintains smooth flow."],
                ["Silicone band", "A soft, skin-friendly strap built for all-day comfort and wear."],
                ["Protective housing", "A compact housing engineered to frame and safeguard the reservoir and electronics."],
              ].map(([t, d]) => (
                <div key={t}>
                  <h4 className="font-heading text-lg font-medium text-white leading-tight">{t}</h4>
                  <p className="font-body text-zinc-400 text-sm leading-relaxed mt-2">{d}</p>
                </div>
              ))}
            </div>
          </div>

          <p className="font-mono text-xs text-zinc-600 mt-10 max-w-3xl leading-relaxed">
            The reservoir and dispensing valve are available today. Sensing, connectivity
            and identification are in development or remain research concepts, shown
            here as the proposed direction of the platform.
          </p>
        </div>
      </section>

      <RFIDSection />

      {/* IP POSITION STATEMENT */}
      <section className="border-t border-zinc-800 bg-[#0d0d0d]" data-testid="technology-patent-position">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent">
              Patent Position
            </span>
            <blockquote className="mt-6 max-w-4xl">
              <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light tracking-tight leading-[1.3] text-zinc-100">
                WearSpray holds issued United States patent protection for a
                wearable dispensing platform that extends into{" "}
                <span className="text-accent">watch-based integration and body sensing</span>.
                We believe this represents one of the earliest issued patent
                positions at the intersection of refillable on-wrist dispensing,
                skincare and sun care access, and biometric wearable technology.
              </p>
            </blockquote>
            <Link
              to="/patents"
              data-testid="technology-patents-link"
              className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase mt-8 text-accent hover:opacity-80 transition-opacity"
            >
              View the issued patents
              <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
